import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import {assert} from 'assert-ts';
import {useNavigate} from 'react-router';
import type {HttpError} from 'api/http/types';
import type {Schema} from 'schemaDefinition/types';
import type {SchemaMode} from 'schemas/types';
import type {NationalAgent, NationalAgentType} from 'services/agent/types';
import type {CodeListId} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {clone} from 'schemaDefinition/functions/clone';
import {validate} from 'schemaDefinition/functions/validators/validate';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {areAgentsEqual} from 'services/agent';
import {useCodelistsLazy} from 'services/codelists';
import {getErrorMessage} from 'services/utils/getErrorMessage';
import {useSnacks} from 'components';
import {useSchemaCodelistIds} from 'schema';
import {useAgentSchema} from 'schemas';
import type {EditorMode} from '../../types';
import type {
  AgentQuery,
  AgentSelection,
  EditAgentWizardProps,
  EmptyResultAlternative,
  Matching,
} from '../types';
import {
  createEmptyNationalAgent,
  getAgentQuery,
  getSavedNationalAgentMessage,
  mergeFromNationalRegistryAgent,
} from '../functions';
import {useAgentSearchSelection} from './useAgentSearchSelection';
import {useDeleteNorafUpdateByAgentId} from './useDeleteNorafUpdateByAgentId';
import type {NationalAgentSearch} from './useNationalAgentSearch';
import {useNationalAgentSearch} from './useNationalAgentSearch';

/**
 * Registrer/rediger aktør med integrasjon mot BARE. Også mulig å opprette bare i Ebba hvis N-ID ikke er satt.
 *
 * N-ID alltid readonly
 * - varianter
 *   1) - registrer ny, søk
 *     a) - finnes ikke i BARE
 *     => - enten: opprett ny både i Ebba og BARE (N-ID tom (vil bli satt))
 *        - enten: mulig å opprette kun i Ebba (N-ID tom (vil ikke bli satt))
 *        - Save: "Registrer person"
 *        v - Checbox: "Registrer i BARE", checked, enabled, !checked => ?onlyEbba query parameter
 *        - i) -> Snack: Registrert i Ebba og BARE
 *        - ii) -> Snack (BARE feilet): Registrert i Ebba, ikke BARE
 *        - iii) -> Snack: Registrert kun i Ebba
 *
 *     b) - valgt treff kun i BARE
 *     => - ta med verdier fra bare, inkl. N-ID, opprett kun i Ebba:
 *        - Save: "Registrer person"
 *        v - Checbox: "Oppdater i BARE", checked, disabled
 *        -> Snack: Registrert i Ebba og koblet til BARE
 *     c) - valgt treff i BARE og Ebba
 *     => Rediger aktør
 *     d) - søk til BARE feiler
 *     => - opprett ny kun i Ebba (N-ID tom (legg til seinere))
 *        - Save: "Registrer person" ?onlyEbba query parameter
 *        v - Checbox: "Oppdater i BARE", unchecked, disabled
 *        -> Snack: Registrert i Ebba, ikke BARE
 *   2) rediger eksisterende
 *     2.1) uten N-ID (fra 1.a.ii, 1.a.iii 1.d),
 *          - vis steg 1, fyll inn navn og andre felt, gjøre søk
 *       a) - finnes ikke i BARE
 *       => - enten: opprett i BARE ved lagring
 *          - eller: lagre kun i Ebba
 *          - Save: "Lagre person"
 *          v - Checbox: "Registrer i BARE", checked, enabled, !checked => ?onlyEbba query parameter
 *          - i) -> Snack: Oppdatert i Ebba og registrert i BARE
 *          - ii) -> Snack (BARE feilet): Oppdatert i Ebba, ikke i BARE
 *          - iii) -> Snack: Oppdatert kun i Ebba
 *       b) - valgt treff i BARE
 *       => - merge verdier fra BARE (inkl. N-ID og ISNI), ikke skriv over verdier i Ebba fra BARE
 *          - Save: "Lagre person"
 *          v - Checbox: "Oppdater i BARE", checked, disabled
 *          -> Snack: Oppdatert i Ebba og koblet til BARE
 *       c) - søk til BARE feiler
 *       => - Save: "Lagre person" ?onlyEbba query parameter
 *          v - Checbox: "Registrer i BARE", unchecked, disabled
 *          -> Snack: Oppdatert i Ebba, ikke BARE
 *     (2.2) alt I: med N-ID, vi går for alt II)
 *     2.2) alt II: med N-ID, vis steg 2
 *       a) - rediger aktør
 *       => - Save: "Lagre person"
 *          v - Checbox: "Oppdater i BARE", checked, disabled
 *          -> Snack: Oppdatert i Ebba og BARE
 *       - Gå til steg 1,
 *          - fyll inn navn og andre felt, gjøre søk,
 *          - automatisk valg av evt. treff med samme N-ID, eller?
 *       b) - valgt treff i BARE (med samme N-ID)
 *       => - merge verdier fra BARE (inkl. N-ID og ISNI), ikke skriv over verdier i Ebba fra BARE
 *          - Save: "Lagre person"
 *          v - Checbox: "Oppdater i BARE", checked, disabled
 *          -> Snack: Oppdatert i Ebba og BARE
 *       c) - valgt treff i BARE (med ulik N-ID)
 *       => - merge verdier fra BARE (inkl. N-ID og ISNI), ikke skriv over verdier i Ebba fra BARE
 *          - Save: "Lagre person"
 *          v - Checbox: "Oppdater i BARE", checked, disabled
 *          -> Snack: Oppdatert i Ebba og re-koblet fra BARE
 *       d) - Behold eksisterende BARE-kobling (ingen av treffene valgt) -> a)
 *       e) - søk til BARE feiler -> a)
 *
 * Tilstandsmaskin
 *   States:
 *   - Aktør:
 * 	   - ny aktør
 * 	     - uten N-ID
 * 	     - med N-ID
 * 	   - eksisterende aktør
 * 	     - uten N-ID
 * 	     - med N-ID
 *   - Steg:
 *     - Steg 1: søk
 *     - Steg 2: rediger
 *   - Matching: ikke match (only ebba hvis checkbox ikke satt), match, failed (only ebba)
 *
 */

const STEP1_ID = 'STEP1';
const STEP2_ID = 'STEP2';

type EditAgentWizardState = {
  step1Id: string;
  step2Id: string;
  currentStep: string;
  agentType: NationalAgentType;
  agentSchema: Schema;
  codelistIds: (CodeListId | LinkedRoleCodeListId)[];
  mode: EditorMode;
  hasNationalId: boolean;
  updateNoraf: boolean;
  setUpdateNoraf: (value: boolean) => void;
  updateNorafDisabled: boolean;
  agentQuery: AgentQuery;
  search: NationalAgentSearch;
  isAutoSearching: boolean;
  selection: AgentSelection;
  emptyResultAlternative: EmptyResultAlternative;
  setEmptyResultAlternative: (value: EmptyResultAlternative) => void;
  // Initial value of agent when going to edit mode
  initialEditAgent: NationalAgent;
  hasChanges: boolean;
  showValidationErrors: boolean;
  setAgentQuery: (agentQuery: AgentQuery) => void;
  gotoStep: (stepId: string) => void;
  agentEdited: (agent: NationalAgent) => void;
  onSave: () => Promise<void>;
  onDelete: () => Promise<void>;
};

export const useEditAgentWizardState = (
  {
    agentType,
    suggestion,
    originalAgent: givenOriginalAgent,
    onSave,
    onDelete,
  }: EditAgentWizardProps,
  schemaMode: SchemaMode,
): EditAgentWizardState => {
  const resolvedAgentType = assert(
    agentType ?? givenOriginalAgent?.agentType,
    'agentType is required',
  );
  const localization = useLocalization();
  const {t, tryT} = localization;
  const navigate = useNavigate();
  const deleteNorafUpdateByAgentId = useDeleteNorafUpdateByAgentId();

  const [originalAgent, setOriginalAgent] = useState<NationalAgent | undefined>(
    givenOriginalAgent,
  );

  const [initialMode] = useState<EditorMode>(
    originalAgent ? 'edit' : 'register',
  );
  const [mode, setMode] = useState<EditorMode>(initialMode);
  const [hasNationalId] = useState<boolean>(
    originalAgent && originalAgent.nationalId ? true : false,
  );
  const [currentStep, setCurrentStep] = useState(
    hasNationalId ? STEP2_ID : STEP1_ID,
  );
  const [agentQuery, setAgentQuery] = useState<AgentQuery>({
    name: suggestion,
  });

  const [initialEditAgent, setInitialEditAgent] = useState(
    originalAgent ?? createEmptyNationalAgent(resolvedAgentType),
  );

  // When registering agent with nationalId, NORAF must be updated.
  const forceUpdateNoraf = useMemo(() => {
    return mode === 'register' && !!initialEditAgent.nationalId;
  }, [initialEditAgent.nationalId, mode]);

  const [updateNoraf, setUpdateNoraf] = useState<boolean>(forceUpdateNoraf);
  const onSetUpdateNoraf = useEffectEvent(setUpdateNoraf);

  useEffect(() => {
    if (forceUpdateNoraf) {
      onSetUpdateNoraf(true);
    }
  }, [forceUpdateNoraf]);

  const search = useNationalAgentSearch(resolvedAgentType);
  const [isAutoSearching, setIsAutoSearching] = useState(false);
  const selection = useAgentSearchSelection(mode);

  const editedAgentRef = useRef<NationalAgent | undefined>(
    hasNationalId ? originalAgent : undefined,
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const handleSetUpdateNoraf = useCallback((value: boolean) => {
    setUpdateNoraf(value);
    if (value) {
      setHasChanges(true);
    }
  }, []);

  const agentSchema = useAgentSchema(
    resolvedAgentType,
    schemaMode,
    initialEditAgent,
  );

  const codelistIds = useSchemaCodelistIds(agentSchema);
  const codelistMap = useCodelistsLazy(codelistIds);

  const {successSnack, errorSnack} = useSnacks();

  const getMatching = useCallback(() => {
    const ids = selection.resultSelection.ids.values();
    const selectedId = ids.next().value;
    const nationalRegistryAgent = search.getNationalRegistryAgent(selectedId);

    const matching: Matching = search.showSearchErrors
      ? 'FAILED'
      : nationalRegistryAgent
        ? 'MATCH'
        : 'NO_MATCH';

    return {matching, nationalRegistryAgent};
  }, [search, selection.resultSelection]);

  const handleAutoSearch = useCallback(
    (agent: NationalAgent) => {
      const query = getAgentQuery(agent);
      if (query.name || query.other) {
        setAgentQuery(query);
        setIsAutoSearching(true);
        search.doSearch(query).finally(() => setIsAutoSearching(false));
      }
    },
    [search, setAgentQuery, setIsAutoSearching],
  );

  const gotoSearchStep = useCallback(() => {
    setCurrentStep(STEP1_ID);
    if (mode !== initialMode && initialMode === 'register') {
      setMode(initialMode);
      setOriginalAgent(undefined);
      setInitialEditAgent(
        createEmptyNationalAgent(resolvedAgentType, agentQuery.name),
      );
    }
    const agent = initialEditAgent ?? originalAgent;
    if (agent && !(agentQuery.name || agentQuery.other)) {
      handleAutoSearch(agent);
    }
  }, [
    agentQuery.name,
    agentQuery.other,
    handleAutoSearch,
    initialEditAgent,
    originalAgent,
    mode,
    initialMode,
    resolvedAgentType,
    setCurrentStep,
    setMode,
    setOriginalAgent,
    setInitialEditAgent,
  ]);

  const gotoEditStep = useCallback(() => {
    const {matching, nationalRegistryAgent} = getMatching();
    // New agent (no original agent)
    if (!originalAgent) {
      switch (matching) {
        case 'MATCH': {
          const ebbaAgent = search.getEbbaAgent(
            nationalRegistryAgent?.nationalId,
          );
          // Change to edit mode if agent already exists in Ebba
          if (ebbaAgent) {
            setOriginalAgent(ebbaAgent);
            // Merge values from national registry agent
            const baseAgent = mergeFromNationalRegistryAgent(
              ebbaAgent,
              assert(
                nationalRegistryAgent,
                'marge: nationalRegistryAgent is required',
              ),
            );
            setInitialEditAgent(baseAgent);
            editedAgentRef.current = baseAgent;
            setMode('edit');
          } else {
            // Base new agent on selected national registry agent
            const baseAgent = {
              ...clone(
                assert(
                  nationalRegistryAgent,
                  'clone: nationalRegistryAgent is required',
                ),
              ),
              id: undefined,
            };
            setInitialEditAgent(baseAgent);
            editedAgentRef.current = baseAgent;
          }
          break;
        }
        case 'NO_MATCH':
        case 'FAILED': {
          // Base new agent on agent type only
          const baseAgent = createEmptyNationalAgent(
            resolvedAgentType,
            agentQuery.name,
          );
          setInitialEditAgent(baseAgent);
          editedAgentRef.current = baseAgent;
          break;
        }
      }
    } else {
      // Edit existing agent
      switch (matching) {
        case 'MATCH': {
          // Merge values from national registry agent
          const baseAgent = mergeFromNationalRegistryAgent(
            originalAgent,
            assert(
              nationalRegistryAgent,
              'marge: nationalRegistryAgent is required',
            ),
          );
          setInitialEditAgent(baseAgent);
          editedAgentRef.current = baseAgent;

          const mergedHasChanges = !areAgentsEqual(originalAgent, baseAgent);
          setHasChanges(mergedHasChanges);
          break;
        }
        case 'NO_MATCH':
        case 'FAILED': {
          // Keep existing values
          setInitialEditAgent(clone(originalAgent));
          editedAgentRef.current = originalAgent;
          break;
        }
      }
    }
    setCurrentStep(STEP2_ID);
  }, [
    getMatching,
    originalAgent,
    resolvedAgentType,
    search,
    setOriginalAgent,
    setInitialEditAgent,
    setMode,
    setHasChanges,
    setCurrentStep,
    agentQuery.name,
  ]);

  const handleGotoStep = useCallback(
    (stepId: string) => {
      if (stepId === currentStep) {
        return;
      }

      stepId === STEP1_ID ? gotoSearchStep() : gotoEditStep();
    },
    [currentStep, gotoEditStep, gotoSearchStep],
  );

  const onSetAgentQuery = useEffectEvent(setAgentQuery);
  const onSetIsAutoSearching = useEffectEvent(setIsAutoSearching);

  useEffect(() => {
    // If editing existing agent and on step 1, fill search criteria and do search.
    // Only run on mount.
    if (originalAgent && currentStep === STEP1_ID) {
      const query = getAgentQuery(originalAgent);
      if (query.name || query.other) {
        onSetAgentQuery(query);
        onSetIsAutoSearching(true);
        search.doSearch(query).finally(() => onSetIsAutoSearching(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAgentEdited = useCallback(
    (agent: NationalAgent) => {
      editedAgentRef.current = agent;
      const newHasChanges =
        mode === 'register' ||
        updateNoraf ||
        !areAgentsEqual(originalAgent, editedAgentRef.current);

      setHasChanges(newHasChanges);

      setInitialEditAgent(agent);
    },
    [mode, originalAgent, updateNoraf],
  );

  const handleSaveAgent = useCallback((): Promise<void> => {
    const validation = validate(
      assert(
        editedAgentRef.current,
        'useEditAgentWizardState:save: edited agent required',
      ),
      {},
      agentSchema,
      assert(codelistMap, 'useEditAgentWizard: codelistMap is required'),
    );

    if (validation.valid !== 'valid') {
      setShowValidationErrors(true);
      return Promise.resolve();
    }

    const {matching} = getMatching();

    return onSave(
      assert(editedAgentRef.current),
      !updateNoraf ? 'bbOnly' : undefined,
    )
      .then(savedAgent => {
        const msg = getSavedNationalAgentMessage(
          originalAgent,
          savedAgent,
          matching,
          updateNoraf,
          t,
        );
        successSnack(msg);
        setInitialEditAgent(savedAgent as NationalAgent);
        setOriginalAgent(savedAgent as NationalAgent);
        editedAgentRef.current = savedAgent as NationalAgent;
        setHasChanges(false);
      })
      .catch(error => {
        const message = getErrorMessage(
          error,
          'page.agents.save.failed',
          localization,
        );
        errorSnack(message);
      })
      .finally(() => {
        // Remove NORAF change notifications on agent.
        if (editedAgentRef.current && editedAgentRef.current.id) {
          deleteNorafUpdateByAgentId(editedAgentRef.current.id);
        }
      });
  }, [
    deleteNorafUpdateByAgentId,
    agentSchema,
    codelistMap,
    errorSnack,
    getMatching,
    localization,
    onSave,
    originalAgent,
    successSnack,
    t,
    updateNoraf,
    setShowValidationErrors,
    setInitialEditAgent,
    setOriginalAgent,
    setHasChanges,
  ]);

  const handleDeleteAgent = useCallback((): Promise<void> => {
    return onDelete(assert(editedAgentRef.current))
      .then(() => {
        successSnack(t('page.agents.delete.success'));
        navigate('/agent');
      })
      .catch((error: HttpError) => {
        const message =
          tryT(`error.api.${error?.message}`) ?? t('error.api.unknown');

        errorSnack(message);
      });
  }, [errorSnack, navigate, onDelete, successSnack, t, tryT]);

  return useMemo(() => {
    const hasNationalId = !!initialEditAgent.nationalId;

    const state: EditAgentWizardState = {
      step1Id: STEP1_ID,
      step2Id: STEP2_ID,
      currentStep,
      agentType: resolvedAgentType,
      agentSchema,
      codelistIds,
      mode,
      hasNationalId,
      updateNoraf,
      setUpdateNoraf: handleSetUpdateNoraf,
      // Search failed => upsert off and disabled to avoid creating duplicates in BARE
      updateNorafDisabled: search.showSearchErrors || forceUpdateNoraf,
      agentQuery,
      search,
      isAutoSearching,
      selection,
      emptyResultAlternative: !updateNoraf ? 'editInternal' : 'createExternal',
      setEmptyResultAlternative: value => {
        handleSetUpdateNoraf(value === 'createExternal');
      },
      initialEditAgent: initialEditAgent,
      hasChanges,
      showValidationErrors,
      setAgentQuery,
      gotoStep: handleGotoStep,
      agentEdited: handleAgentEdited,
      onSave: handleSaveAgent,
      onDelete: handleDeleteAgent,
    };

    return state;
  }, [
    initialEditAgent,
    currentStep,
    resolvedAgentType,
    agentSchema,
    codelistIds,
    mode,
    updateNoraf,
    handleSetUpdateNoraf,
    search,
    forceUpdateNoraf,
    agentQuery,
    isAutoSearching,
    selection,
    hasChanges,
    showValidationErrors,
    handleGotoStep,
    handleAgentEdited,
    handleSaveAgent,
    handleDeleteAgent,
    setAgentQuery,
  ]);
};
